import {Link} from '@tanstack/react-router'
import clsx from 'clsx'

import ArrowRightSvg from '@/assets/homepage-business/arrow-right.svg?react'
import avatar1OptimizedImg from '@/assets/homepage-business/avatar1.png?minW=80&picture-image-tools'
import avatar2OptimizedImg from '@/assets/homepage-business/avatar2.png?minW=80&picture-image-tools'
import avatar3OptimizedImg from '@/assets/homepage-business/avatar3.png?minW=80&picture-image-tools'
import avatar4OptimizedImg from '@/assets/homepage-business/avatar4.png?minW=80&picture-image-tools'
import avatar5OptimizedImg from '@/assets/homepage-business/avatar5.png?minW=80&picture-image-tools'
import ChatSvg from '@/assets/homepage-business/chat.svg?react'
import ClickSvg from '@/assets/homepage-business/click.svg?react'
import coBrowseOptimizedImg from '@/assets/homepage-business/CoBrowse.png?minW=400&picture-image-tools'
import coBrowseButtonOptimizedImg from '@/assets/homepage-business/CoBrowseButton.png?minW=200&picture-image-tools'
import costBodyOptimizedImg from '@/assets/homepage-business/costImage.png?minW=400&picture-image-tools'
import desktopStorefrontOptimizedImg from '@/assets/homepage-business/desktopStorefront.png?minW=600&picture-image-tools'
import DisabledSeniorsSvg from '@/assets/homepage-business/disabledSenior.svg?react'
import FasterSvg from '@/assets/homepage-business/faster.svg?react'
import getStartedOptimizedImg from '@/assets/homepage-business/getStarted.png?minW=400&picture-image-tools'
import heroBackgroundDarkLqip from '@/assets/homepage-business/heroBackground-dark.png?lqip'
import heroBackgroundDarkImg from '@/assets/homepage-business/heroBackground-dark.png?minW=1024&picture-image-tools'
import heroBackgroundLightLqip from '@/assets/homepage-business/heroBackground-light.png?lqip'
import heroBackgroundLightImg from '@/assets/homepage-business/heroBackground-light.png?minW=1024&picture-image-tools'
import InclusiveSvg from '@/assets/homepage-business/inclusive.svg?react'
import LineSvg from '@/assets/homepage-business/line.svg?react'
import MemorableSvg from '@/assets/homepage-business/memorable.svg?react'
import MobileSvg from '@/assets/homepage-business/mobile.svg?react'
import MoneyLostSvg from '@/assets/homepage-business/moneyLost.svg?react'
import PoorUsabilitySvg from '@/assets/homepage-business/poorUsability.svg?react'
import scaleAndImpactOptimizedImg from '@/assets/homepage-business/scaleAndImpact.png?minW=500&picture-image-tools'
import SmarterSvg from '@/assets/homepage-business/smarter.svg?react'
import StoreSvg from '@/assets/homepage-business/store.svg?react'
import TickSvg from '@/assets/homepage-business/tick.svg?react'
import twoPhonesOptimizedImg from '@/assets/homepage-business/twoPhones.png?minW=375&picture-image-tools'
import WaveSvg from '@/assets/homepage-business/wave.svg?react'
import ChevronRightSvg from '@/assets/icons/chevron-right.svg?react'
import acbImg from '@/assets/images/media-mentions/acb.png'
import acbDarkImg from '@/assets/images/media-mentions/acb-dark.png'
import afbImg from '@/assets/images/media-mentions/afb.png'
import afbDarkImg from '@/assets/images/media-mentions/afb-dark.png'
import beMyEyesImg from '@/assets/images/media-mentions/bemyeyes.png'
import beMyEyesDarkImg from '@/assets/images/media-mentions/bemyeyes-dark.png'
import blindlifeImg from '@/assets/images/media-mentions/blindlife.png'
import blindlifeDarkImg from '@/assets/images/media-mentions/blindlife-dark.png'
import brailleInstituteImg from '@/assets/images/media-mentions/braille-institute.png'
import brailleInstituteDarkImg from '@/assets/images/media-mentions/braille-institute-dark.png'
import carrieImg from '@/assets/images/media-mentions/carrieonaccessibility.png'
import carrieDarkImg from '@/assets/images/media-mentions/carrieonaccessibility-dark.png'
import doubletapImg from '@/assets/images/media-mentions/doubletap.png'
import doubletapDarkImg from '@/assets/images/media-mentions/doubletap-dark.png'
import eyesOnSuccessImg from '@/assets/images/media-mentions/eyesonsuccess.png'
import eyesOnSuccessDarkImg from '@/assets/images/media-mentions/eyesonsuccess-dark.png'
import nfbImg from '@/assets/images/media-mentions/nfb.png'
import nfbDarkImg from '@/assets/images/media-mentions/nfb-dark.png'
import vistaCenterImg from '@/assets/images/media-mentions/vista-center.png'
import vistaCenterDarkImg from '@/assets/images/media-mentions/vista-center-dark.png'
import Picture from '@/components/Picture'
import Footer from '@/layout/Footer'
import Navbar from '@/layout/Navbar'
import HeadTags from '@/lib/head/HeadTags'
import {Theme} from '@/lib/theme/theme'
import {useCurrentTheme} from '@/lib/theme/useCurrentTheme'
import skipTargetProps from '@/utils/a11y/skipTargetProps'
import VisuallyHidden from '@/utils/a11y/VisuallyHidden'

import style from './Business.module.scss'

interface TrustedLogo {
  light: string
  dark: string
  alt: string
  big?: boolean
}

const TRUSTED_BY_LOGOS: readonly TrustedLogo[] = [
  {
    light: beMyEyesImg,
    dark: beMyEyesDarkImg,
    alt: 'Be My Eyes',
    big: false,
  },
  {
    light: nfbImg,
    dark: nfbDarkImg,
    alt: 'National Federation of the Blind',
    big: false,
  },
  {
    light: acbImg,
    dark: acbDarkImg,
    alt: 'American Council of the Blind',
    big: false,
  },
  {
    light: afbImg,
    dark: afbDarkImg,
    alt: 'American Foundation for the Blind',
    big: false,
  },
  {
    light: doubletapImg,
    dark: doubletapDarkImg,
    alt: 'DoubleTap',
    big: false,
  },
  {
    light: brailleInstituteImg,
    dark: brailleInstituteDarkImg,
    alt: 'Braille Institute',
    big: false,
  },
  {
    light: vistaCenterImg,
    dark: vistaCenterDarkImg,
    alt: 'Vista Center',
    big: false,
  },
  {
    light: carrieImg,
    dark: carrieDarkImg,
    alt: 'Carrie on Accessibility',
    big: true,
  },
  {
    light: eyesOnSuccessImg,
    dark: eyesOnSuccessDarkImg,
    alt: 'Eyes on Success',
    big: true,
  },
  {
    light: blindlifeImg,
    dark: blindlifeDarkImg,
    alt: 'Blind Life',
    big: false,
  },
] as const

const CALENDLY_URL = 'https://calendly.com/innosearch-patrick/' as const

const CONVERSION_CARDS = [
  {
    title: 'Increase Conversions',
    description: 'Reduce cart abandonment by up to 70% for seniors and disabled users.',
  },
  {
    title: 'Unlock New Revenue',
    description:
      'Reach the $10T+ in annual spending power from audiences underserved by traditional sites.',
  },
] as const

const CoBrowseImages = () => (
  <>
    <Picture
      src={{
        pic: coBrowseOptimizedImg,
      }}
      sizes='(max-width: 1023px) 80vw, 40vw'
      alt=''
      className={style.CobrowseImage}
      loading='lazy'
    />
    <Picture
      src={{
        pic: coBrowseButtonOptimizedImg,
      }}
      sizes='(max-width: 1023px) 40vw, 20vw'
      alt=''
      className={style.CobrowseButton}
      loading='lazy'
    />
  </>
)

const HOW_IT_WORKS_CARDS = [
  {
    icon: <ChatSvg aria-hidden />,
    description: 'Customers discover products simply by talking to your website.',
  },
  {
    icon: <ClickSvg aria-hidden />,
    description: 'Our AI guides them through your checkout without a single click.',
  },
  {
    icon: <MobileSvg aria-hidden />,
    description: 'Works instantly on website, app, or phone with no redesign.',
  },
] as const

const EASY_STEPS = [
  {num: '1', desc: 'Add our one-line JavaScript snippet.'},
  {num: '2', desc: 'Go live. Instant voice navigation and checkout.'},
] as const

const COST_CARDS = [
  {
    icon: <DisabledSeniorsSvg aria-hidden />,
    title: '70% of disabled/senior users',
    description: 'abandon purchases if checkout is difficult.',
  },
  {
    icon: <PoorUsabilitySvg aria-hidden />,
    title: 'Poor usability raises bounce rates by 123%',
    description: '(Nielsen Norman Group).',
  },
  {
    icon: <MoneyLostSvg aria-hidden />,
    title: 'That’s $21B/year lost',
    description: 'from disabled users alone (Click-Away Pound Report).',
  },
] as const

const SOLUTION_CARDS = [
  {
    icon: <FasterSvg />,
    title: 'Faster',
    description: 'Natural language is 3× quicker than typing for most users',
  },
  {
    icon: <SmarterSvg />,
    title: 'Smarter',
    description: 'Handles discovery, help, and checkout in one conversation.',
  },
  {
    icon: <InclusiveSvg />,
    title: 'Inclusive',
    description: 'Works for mobile shoppers, multitaskers, seniors, and disabled users.',
  },
  {
    icon: <MemorableSvg />,
    title: 'Memorable',
    description: 'Customers retain 50% more from spoken interaction than reading text.',
  },
] as const

const SAAS_FEATURES = [
  'Instantly serve new markets without rebuilding your site',
  'Improve brand image with accessibility that actually works',
  'Plug into your existing inventory and backend with no downtime',
] as const

const SAY_CARDS = [
  {
    avatar: avatar1OptimizedImg,
    speech: 'Find gluten-free snacks under $20',
    result: 'Product Discovery in 5 seconds.',
  },
  {
    avatar: avatar2OptimizedImg,
    speech: 'Show me the checkout page',
    result: 'Skip 4–5 clicks instantly.',
  },
  {
    avatar: avatar3OptimizedImg,
    speech: 'Does this TV have Bluetooth?',
    result: 'Zero hunting through product specs.',
  },
  {
    avatar: avatar4OptimizedImg,
    speech: 'Add it to my cart and pay with my saved card',
    result: 'Checkout in under 30 seconds.',
  },
  {
    avatar: avatar5OptimizedImg,
    speech: 'Repeat my last order',
    result: 'Frictionless reorders for recurring revenue.',
  },
] as const

const SCALE_IMPACT_CARDS = [
  {
    title: '10 Trillion+',
    text: 'Spending Power controlled by the Disabled and Seniors',
  },
  {
    title: '1–2 hours',
    text: 'saved per shopping trip, per customer',
  },
  {
    title: '71%',
    text: 'disabled or seniors will leave a website they find difficult to use, and never return',
  },
] as const

export default function Business() {
  const [currentTheme] = useCurrentTheme()

  return (
    <div className={style.Container}>
      <div className={style.Page}>
        <HeadTags title='Business' />
        <Navbar variant='dark' haveCart={false} />
        <main {...skipTargetProps('main-content')}>
          <VisuallyHidden as='h1'>Innosearch AI for Business</VisuallyHidden>
          <section className={style.HeroSection} aria-labelledby='business-hero-heading'>
            <Picture
              src={{
                pic: currentTheme === Theme.Dark ? heroBackgroundDarkImg : heroBackgroundLightImg,
                lqip:
                  currentTheme === Theme.Dark ? heroBackgroundDarkLqip : heroBackgroundLightLqip,
              }}
              fetchPriority='high'
              alt=''
              className={style.HeroBackground}
              preload
            />
            <div className={style.HeroContent}>
              <h2 id='business-hero-heading' className={style.Title}>
                <span className={style.Line1}>Add Voice. Expand Your Reach.</span>
                <span className={style.Line2}>Increase Your Sales.</span>
              </h2>
              <p className={style.Description}>
                Disabled and senior customers control over $10T in spending, but most sites aren't
                accessible or easy to use.
                <br />
                Our AI-powered voice makes the web simple, taking them from 'I'm looking for…' to
                'Order placed' in seconds.
              </p>
              <div className={style.CtaButtonsContainer}>
                <a
                  href={CALENDLY_URL}
                  target='_blank'
                  rel='noopener noreferrer'
                  className={clsx(style.CtaLink, style.HeroCta)}
                  aria-label='Book a demo with InnoSearch'
                >
                  Book the demo
                </a>
                <Link to='/pricing' className={clsx(style.CtaLink, style.HeroCtaSecondary)}>
                  View Pricing
                </Link>
              </div>
              <Picture
                src={{
                  pic: twoPhonesOptimizedImg,
                }}
                sizes='(max-width: 1023px) min(90%, 37.2867rem), 37.2867rem'
                alt='Accessible Digital Services and Storefront on mobile'
                className={style.Phones}
                loading='lazy'
              />
            </div>
          </section>

          <section className={style.TrustedBySection} aria-labelledby='trusted-by-heading'>
            <VisuallyHidden as='h2' id='trusted-by-heading'>
              Trusted by organizations
            </VisuallyHidden>

            <VisuallyHidden>
              <p>We are trusted by the following organizations:</p>
              <ul>
                {TRUSTED_BY_LOGOS.map(logo => (
                  <li key={`sr-${logo.alt}`}>{logo.alt}</li>
                ))}
              </ul>
            </VisuallyHidden>

            <div aria-hidden='true' className={style.TrustedByMarquee}>
              <div className={style.TrustedByTrack}>
                {/* First set */}
                <div className={style.TrustedByContent}>
                  <div className={style.TrustedText}>TRUSTED BY</div>
                  {TRUSTED_BY_LOGOS.map(logo => (
                    <img
                      key={logo.alt}
                      src={currentTheme === Theme.Dark ? logo.dark : logo.light}
                      alt=''
                      className={clsx(style.TrustedByLogo, logo.big && style.TrustedByLogoBig)}
                      loading='lazy'
                    />
                  ))}
                </div>
                {/* Second set */}
                <div className={style.TrustedByContent}>
                  <div className={style.TrustedText}>TRUSTED BY</div>
                  {TRUSTED_BY_LOGOS.map(logo => (
                    <img
                      key={`${logo.alt}-2`}
                      src={currentTheme === Theme.Dark ? logo.dark : logo.light}
                      alt=''
                      className={clsx(style.TrustedByLogo, logo.big && style.TrustedByLogoBig)}
                      loading='lazy'
                    />
                  ))}
                </div>
                {/* Third set */}
                <div className={style.TrustedByContent}>
                  <div className={style.TrustedText}>TRUSTED BY</div>
                  {TRUSTED_BY_LOGOS.map(logo => (
                    <img
                      key={`${logo.alt}-3`}
                      src={currentTheme === Theme.Dark ? logo.dark : logo.light}
                      alt=''
                      className={clsx(style.TrustedByLogo, logo.big && style.TrustedByLogoBig)}
                      loading='lazy'
                    />
                  ))}
                </div>
              </div>
            </div>
          </section>

          <section className={style.FromSearchToSaleSection} aria-labelledby='from-search-to-sale'>
            <div className={style.FromSearchToSaleTextAndCobrowse}>
              <div>
                <h2
                  id='from-search-to-sale'
                  className={style.FromSearchToSaleHeading}
                  aria-label='From Search to Sale. Without the Friction.'
                >
                  <span className={style.FromSearchToSaleLine1}>
                    From <span className={style.FromSearchToSaleHighlight}>Search to Sale</span>.
                  </span>
                  <span className={style.FromSearchToSaleLine2}>Without the Friction</span>
                </h2>
                <div className={style.FromSearchToSaleBody}>
                  <p className={style.ConversionsText}>
                    <strong className={style.ConversionsInlineBold}>
                      Every extra click costs you conversions.
                    </strong>{' '}
                    Cobrowse AI removes the barriers by letting customers navigate, search, and
                    complete purchases entirely by voice.
                  </p>
                  <div
                    className={`${style.CobrowseImageWrapper} ${style.CobrowseImageWrapperMobile}`}
                    role='img'
                    aria-label='A screenshot of the CoBrowse AI chat interface, demonstrating how users can ask questions and get immediate answers.'
                  >
                    <CoBrowseImages />
                  </div>
                  <h3 className={style.FlagshipTitle}>Our Flagship Service Cobrowse AI</h3>
                  <div className={style.FlagshipCards}>
                    {CONVERSION_CARDS.map(card => (
                      <div key={card.title} className={style.InfoCard}>
                        <h4 className={style.InfoCardTitle}>{card.title}</h4>
                        <div className={style.InfoCardDesc}>{card.description}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div
                className={`${style.CobrowseImageWrapper} ${style.CobrowseImageWrapperDesktop}`}
                role='img'
                aria-label='A screenshot of the CoBrowse AI chat interface, demonstrating how users can ask questions and get immediate answers.'
              >
                <CoBrowseImages />
              </div>
            </div>
            <div className={style.HowItWorks} aria-labelledby='how-it-works'>
              <h3 id='how-it-works' className={style.HowItWorksTitle}>
                How It Works:
              </h3>
              <div className={style.HowItWorksCardsContainer}>
                {HOW_IT_WORKS_CARDS.map(step => (
                  <div key={step.description} className={style.HowItWorksCard}>
                    <div aria-hidden>{step.icon}</div>
                    <div className={style.HowItWorksCardDesc}>{step.description}</div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className={style.EasyIntegrationSection} aria-labelledby='easy-integration'>
            <div className={style.EasyIntegrationContainer}>
              <div>
                <h2 id='easy-integration' className={style.EasyIntegrationTitle}>
                  Easy Integration
                </h2>
                <p className={style.EasyIntegrationSubtitle}>Go live in two simple steps:</p>
              </div>
              <div className={style.EasyIntegrationStepsContainer}>
                <LineSvg className={style.LineConnector} aria-hidden />
                <ol className={style.EasyIntegrationStepsList}>
                  {EASY_STEPS.map(({num, desc}) => (
                    <li key={num} className={style.IndividualStep}>
                      <div className={style.StepNumber} aria-hidden>
                        {num}
                      </div>
                      <div className={style.StepDescription}>{desc}</div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>
          </section>

          <section className={style.CostSection} aria-labelledby='cost'>
            <h2 id='cost' className={style.CostTitle}>
              The Cost of Hard-to-Use Sites
            </h2>
            <div className={style.CostBody}>
              <div className={style.CostBodyLeftImage}>
                <Picture
                  src={{
                    pic: costBodyOptimizedImg,
                  }}
                  sizes='(max-width: 1023px) 90vw, 50vw'
                  alt='A frustrated user looking at a laptop, representing the high cost of a poorly designed website.'
                  loading='lazy'
                />
              </div>
              <div className={style.CostBodyRightText}>
                {COST_CARDS.map(cost => (
                  <div key={cost.title} className={style.CostCard}>
                    <div className={style.CostCardIcon} aria-hidden>
                      {cost.icon}
                    </div>
                    <div className={style.CostCardContent}>
                      <div className={style.CostCardTitle}>{cost.title}</div>
                      <div className={style.CostCardDesc}>{cost.description}</div>
                    </div>
                  </div>
                ))}

                <div className={style.CostCallout}>
                  <div className={style.CostCalloutIcon}>
                    <ChevronRightSvg aria-hidden />
                  </div>
                  <span className={style.CostCalloutText}>
                    These aren’t niche customers. Seniors, disabled users, and neurodiverse people
                    represent <span className={style.CostCalloutEmphasis}>over $10 trillion</span>{' '}
                    in spending power.
                  </span>
                </div>
              </div>
            </div>
          </section>

          <section className={style.SolutionSection} aria-labelledby='solution'>
            <div>
              <h2 id='solution' className={style.SolutionTitle}>
                Why Innosearch AI is the Solution
              </h2>
              <p className={style.SolutionSubTitle}>
                With CoBrowse, your site works like a skilled in-store associate but online, and
                available 24/7.
              </p>
            </div>
            <div className={style.SolutionCards}>
              {SOLUTION_CARDS.map(solution => (
                <div key={solution.title} className={style.SolutionCard}>
                  <div className={style.SolutionIcon} aria-hidden>
                    {solution.icon}
                  </div>
                  <h3 className={style.SolutionCardTitle}>{solution.title}</h3>
                  <p className={style.SolutionCardDesc}>{solution.description}</p>
                </div>
              ))}
            </div>
          </section>
          <section className={style.SaasSection} aria-labelledby='saas'>
            <h2 id='saas' className={style.SaasTitle}>
              InnoShop - Storefront-as-a-Service
            </h2>
            <p className={style.SaasSubtitle}>
              For businesses without a fully accessible store, InnoShop delivers a ready-to-run,
              voice-enabled storefront.
            </p>
            <div className={style.SaasImageContainer}>
              <div className={style.PinkSquare} aria-hidden />
              <Picture
                src={{
                  pic: desktopStorefrontOptimizedImg,
                }}
                sizes='(max-width: 1023px) 90vw, 60vw'
                alt='A preview of the InnoShop storefront on a desktop computer, showcasing a clean and modern e-commerce interface.'
                className={style.SaasImage}
                loading='lazy'
              />
              <div className={style.CheckItOutBox} aria-hidden>
                <div className={style.CheckItOutIcon} aria-hidden>
                  <StoreSvg />
                </div>
                <div className={style.CheckItOutTextGroup}>
                  <div className={style.CheckItOutEyebrow}>Check it out!</div>
                  <div className={style.CheckItOutHeadline}>Our new storefront is live!</div>
                </div>
              </div>
            </div>
            <div className={style.SaasFeatures} role='list'>
              {SAAS_FEATURES.map(text => (
                <div key={text} className={style.SaasFeature} role='listitem'>
                  <div className={style.SaasFeatureIcon} aria-hidden>
                    <TickSvg />
                  </div>
                  <div className={style.SaasFeatureTitle}>{text}</div>
                </div>
              ))}
            </div>
          </section>

          <section
            className={style.WhatCustomersCanSaySection}
            aria-labelledby='what-customers-can-say'
          >
            <h2 className={style.WhatCustomersCanSayTitle} id='what-customers-can-say'>
              What Customers Can Say
              <div className={style.WhatCustomersCanSaySubtitle}>
                Minutes of clicking become seconds of conversation
              </div>
            </h2>

            <div className={style.WhatCustomersCanSayCards}>
              {SAY_CARDS.map(item => (
                <div
                  key={item.speech}
                  className={style.SayCard}
                  role='figure'
                  aria-label={`A customer says, "${item.speech}," which results in: "${item.result}."`}
                >
                  <div className={style.SayTopRow}>
                    <Picture
                      src={{
                        pic: item.avatar,
                      }}
                      sizes='80px'
                      alt=''
                      className={style.Avatar}
                      loading='lazy'
                    />
                    <div className={style.Speech} aria-hidden>
                      {item.speech}
                    </div>
                  </div>
                  <div className={style.Result} aria-hidden>
                    <ArrowRightSvg aria-hidden /> {item.result}
                  </div>
                </div>
              ))}
            </div>
          </section>

          <section className={style.ScaleAndImpactSection} aria-labelledby='scale-and-impact'>
            <h2 id='scale-and-impact' className={style.ScaleAndImpactTitle}>
              Scale and Impact
            </h2>
            <div className={style.ScaleAndImpactContainer}>
              <div className={style.ScaleAndImpactCards}>
                {SCALE_IMPACT_CARDS.map(card => (
                  <div key={card.title} className={style.ScaleAndImpactCard}>
                    <div className={style.ScaleAndImpactCardTitle}>{card.title}</div>
                    <div className={style.ScaleAndImpactCardText}>{card.text}</div>
                  </div>
                ))}
              </div>
              <div>
                <Picture
                  src={{
                    pic: scaleAndImpactOptimizedImg,
                  }}
                  sizes='(max-width: 1023px) 90vw, 50vw'
                  alt='A collage of diverse people happily using technology, illustrating the broad scale and positive impact of accessible design.'
                  className={style.ScaleAndImpactImage}
                  loading='lazy'
                />
              </div>
            </div>
          </section>
          <section className={style.GetStartedSection} aria-labelledby='get-started'>
            <div className={style.WaveContainer}>
              <WaveSvg aria-hidden />
            </div>
            <div className={style.GetStartedContainer}>
              <div className={style.ContentContainer}>
                <h2 id='get-started' className={style.ContentTitle}>
                  Get started now
                </h2>
                <p className={style.ContentSubtitle}>
                  With Cobrowse AI, your site works like a skilled in-store associate but online,
                  and available 24/7.
                </p>
                <div className={style.CtaButtonsContainer}>
                  <a
                    href={CALENDLY_URL}
                    target='_blank'
                    rel='noopener noreferrer'
                    className={clsx(style.BookFreePilotLink, style.GetStartedButton)}
                    aria-label='Book a free pilot with InnoSearch'
                  >
                    Book a free Pilot
                  </a>
                  <Link
                    to='/pricing'
                    className={clsx(style.BookFreePilotLink, style.GetStartedButton)}
                  >
                    View Pricing
                  </Link>
                </div>
                <div className={style.SeeResultsIn30Days}>See results in 30 days</div>
              </div>
              <div>
                <Picture
                  src={{
                    pic: getStartedOptimizedImg,
                  }}
                  sizes='(max-width: 1023px) 90vw, 50vw'
                  alt='A woman smiling and pointing at a laptop screen, encouraging users to get started with the service.'
                  className={style.GetStartedImg}
                  loading='lazy'
                />
              </div>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </div>
  )
}
